import React, { useCallback, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useStore } from '@store/StoreProvider'
import { toastStore } from '@store/ToastStore'
import {
  resendEmailVerification,
  RESEND_COOLDOWN_MS,
  EmailVerificationResendRateLimitError,
  formatResendWaitLabel,
} from '@/api/emailVerificationApi'
import { EMAIL_VERIFICATION_PROFILE_PATH } from '@store/EmailVerificationStore'
import { t } from '@store/I18nStore'

const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/reset',
  '/reset-password',
  '/activate',
  '/auth/verify-email',
  '/auth/accept-invite',
  '/offer',
  '/terms',
  '/policy',
  '/status',
]

const KNOWN_APP_PREFIXES = [
  ...PUBLIC_PATHS,
  '/knowledge-base',
  '/dialogs',
  '/visitors',
  '/control',
  '/team',
  '/myProfile',
  '/settings',
  '/statistics',
  '/billing',
]

function isPublicAuthPath(pathname: string): boolean {
  if (pathname === '/' || pathname.startsWith('/knowledge-base')) return true
  if (PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))) return true
  // Unknown URLs render the public 404 page — do not redirect away from it.
  return !KNOWN_APP_PREFIXES.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

function isProfilePath(pathname: string): boolean {
  return pathname === EMAIL_VERIFICATION_PROFILE_PATH || pathname.startsWith(`${EMAIL_VERIFICATION_PROFILE_PATH}/`)
}

function needsEmailVerification(blocked: boolean, emailVerified?: boolean): boolean {
  return blocked || emailVerified === false
}

/** Sends unverified operators to profile instead of a blocking modal. Closed pages do not mount. */
export const EmailVerificationGate: React.FC<{ children?: React.ReactNode }> = observer(({ children }) => {
  const { auth, emailVerification } = useStore()
  const location = useLocation()

  if (!auth.isAuthenticated) return <>{children}</>
  if (!needsEmailVerification(emailVerification.blocked, auth.currentUser?.emailVerified)) {
    return <>{children}</>
  }
  if (isPublicAuthPath(location.pathname) || isProfilePath(location.pathname)) {
    return <>{children}</>
  }

  return <Navigate to={EMAIL_VERIFICATION_PROFILE_PATH} replace />
})

function resendCooldownStorageKey(userId: number | undefined): string {
  return `email_verify_resend_until_${userId ?? 'anon'}`
}

function readResendCooldownUntil(userId: number | undefined): number {
  try {
    const raw = localStorage.getItem(resendCooldownStorageKey(userId))
    if (!raw) return 0
    const until = Number(raw)
    if (!Number.isFinite(until) || until <= Date.now()) {
      localStorage.removeItem(resendCooldownStorageKey(userId))
      return 0
    }
    return until
  } catch {
    return 0
  }
}

function writeResendCooldownUntil(userId: number | undefined, until: number): void {
  try {
    if (until <= Date.now()) {
      localStorage.removeItem(resendCooldownStorageKey(userId))
      return
    }
    localStorage.setItem(resendCooldownStorageKey(userId), String(until))
  } catch {
    /* ignore quota / private mode */
  }
}

/** Red notice on the profile page: confirm email + resend. */
export const EmailUnverifiedBanner: React.FC = observer(() => {
  const { auth, i18n, emailVerification } = useStore()
  const userId = auth.currentUser?.id
  const [resendPending, setResendPending] = useState(false)
  const [resendCooldownUntil, setResendCooldownUntil] = useState(() =>
    readResendCooldownUntil(auth.currentUser?.id),
  )
  const [, setTick] = useState(0)

  const visible =
    auth.isAuthenticated &&
    needsEmailVerification(emailVerification.blocked, auth.currentUser?.emailVerified)

  const resendOnCooldown = resendCooldownUntil > Date.now()
  const cooldownSec = resendOnCooldown
    ? Math.max(1, Math.ceil((resendCooldownUntil - Date.now()) / 1000))
    : 0

  useEffect(() => {
    if (!visible) return
    setResendCooldownUntil(readResendCooldownUntil(userId))
  }, [visible, userId])

  useEffect(() => {
    if (resendCooldownUntil <= Date.now()) return
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [resendCooldownUntil])

  const startCooldown = useCallback((ms: number) => {
    const wait = Math.max(1000, ms)
    const until = Date.now() + wait
    setResendCooldownUntil(until)
    writeResendCooldownUntil(userId, until)
  }, [userId])

  const handleResend = useCallback(async () => {
    if (resendPending || resendOnCooldown) return
    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    if (!base) return
    setResendPending(true)
    try {
      const message = await resendEmailVerification(base, auth.authHeader)
      toastStore.show(message, 'success')
      startCooldown(RESEND_COOLDOWN_MS)
    } catch (err) {
      if (err instanceof EmailVerificationResendRateLimitError) {
        startCooldown(err.retryAfterSeconds * 1000)
        toastStore.show(err.message, 'warning')
        return
      }
      const text = err instanceof Error ? err.message : t('errors.auth.send_email')
      toastStore.show(text, 'error')
    } finally {
      setResendPending(false)
    }
  }, [auth.authHeader, i18n.apiBaseUrl, resendOnCooldown, resendPending, startCooldown])

  if (!visible) return null

  return (
    <div className="mp-verify-banner" role="alert">
      <span className="mp-verify-banner-text">
        {emailVerification.message || t('auth.confirm_email.title')}
      </span>
      <button
        type="button"
        className="mp-verify-banner-btn"
        onClick={() => void handleResend()}
        disabled={resendPending || resendOnCooldown}
      >
        {resendPending
          ? t('app.status.sending')
          : resendOnCooldown
            ? t('auth.resend_in', { time: formatResendWaitLabel(cooldownSec) })
            : t('app.actions.resend')}
      </button>
    </div>
  )
})
